import type { ProviderIdInput } from "@postdaily/api/schemas/social-accounts";
import type { PlatformId } from "@/components/social-accounts/provider-icon";
import { PROVIDERS_IN_REVIEW } from "./connect-providers";
import { joinWithAnd } from "./join-with-and";

/** Marketing status: a shipped adapter vs a "coming soon" network. */
export type ChannelStatus = "live" | "soon";

/**
 * Single source of truth for the Channels hub connect grid AND the marketing
 * site (the Landing "every network" showcase + the Integrations cards). It
 * lists every platform we advertise — including ones whose adapters have not
 * shipped yet — so the grid mirrors Buffer/Publer. Whether a platform is
 * actually connectable is decided at runtime from
 * `socialAccounts.providerCapabilities`, not from this static list.
 */
export type PlatformCatalogEntry = {
  id: PlatformId;
  label: string;
  /** Set when this platform maps to a real, connectable provider id. */
  providerId?: ProviderIdInput;
  /** Short "follow us"-style blurb shown in the per-platform connect dialog. */
  blurb?: string;
  /** Marketing: shipped ("live") vs "coming soon" ("soon"). */
  status: ChannelStatus;
  /** Short descriptor under the name (Landing showcase + Integrations card). */
  tagline: string;
  /** One-line marketing description (Integrations card body). */
  description: string;
  /** Capability chips (Integrations card). */
  capabilities: string[];
};

export const PLATFORM_CATALOG: PlatformCatalogEntry[] = [
  {
    id: "bluesky",
    label: "Bluesky",
    providerId: "bluesky",
    blurb: "Connect your Bluesky (AT Protocol) account to schedule posts.",
    status: "live",
    tagline: "AT Protocol",
    description:
      "Schedule posts, threads, images and video to your Bluesky profile — with a live preview of the butterfly card.",
    capabilities: ["300 chars", "Images", "Video", "Threads"],
  },
  {
    id: "mastodon",
    label: "Mastodon",
    providerId: "mastodon",
    blurb: "Connect any Mastodon server to schedule posts and threads.",
    status: "live",
    tagline: "Fediverse",
    description:
      "Publish posts, threads, images and video to any Mastodon server over OAuth — no API keys required.",
    capabilities: ["500 chars", "Media", "Threads"],
  },
  {
    id: "x",
    label: "X",
    providerId: "x",
    status: "live",
    tagline: "Twitter",
    description:
      "Publish posts, threads and media to X, with native previews and character counting.",
    capabilities: ["280 chars", "Media", "Threads"],
  },
  {
    id: "linkedin",
    label: "LinkedIn",
    providerId: "linkedin",
    // Live, but still in LinkedIn's app review: see `platformInReview`.
    status: "live",
    tagline: "Pages & profile",
    description:
      "Post to your personal profile and company pages, with document and article support.",
    capabilities: ["3,000 chars", "Docs", "Video"],
  },
  {
    id: "instagram",
    label: "Instagram",
    providerId: "instagram",
    blurb:
      "Connect an Instagram business or creator account to schedule posts, carousels and Reels.",
    // Meta App Review approved the Instagram publishing scopes (2026-08-14)
    // and the app is Live. Runtime connectability is still gated by
    // ENABLE_INSTAGRAM_PROVIDER via providerCapabilities.
    status: "live",
    tagline: "Reels & posts",
    description:
      "Schedule feed posts, carousels and reels with first-comment and hashtag support.",
    capabilities: ["2,200 chars", "Reels", "Carousel"],
  },
  {
    id: "facebook",
    label: "Facebook",
    providerId: "facebook",
    blurb: "Connect your Facebook Pages to schedule posts, photos and video.",
    status: "live",
    tagline: "Pages",
    description:
      "Publish long-form posts, photos, videos and link previews to your Facebook pages.",
    capabilities: ["Long-form", "Video", "Links"],
  },
  {
    id: "tiktok",
    label: "TikTok",
    providerId: "tiktok",
    blurb:
      "Connect your TikTok account to Direct Post videos and photo carousels.",
    // TikTok approved the Content Posting API audit (2026-08-23) and the app is
    // Live, so Direct Post publishes at the creator's chosen privacy level.
    // Runtime connectability is still gated by ENABLE_TIKTOK_PROVIDER via
    // providerCapabilities.
    status: "live",
    tagline: "Video",
    description:
      "Direct Post short-form videos and photo carousels to TikTok with captions, privacy controls and cover selection.",
    capabilities: ["Video", "Photos", "Captions"],
  },
  {
    id: "threads",
    label: "Threads",
    providerId: "threads",
    blurb: "Connect your Threads profile to schedule posts and reply-threads.",
    // Meta App Review approved the Threads scopes (2026-08-14) and the app is
    // Live. Runtime connectability is still gated by ENABLE_THREADS_PROVIDER
    // via providerCapabilities.
    status: "live",
    tagline: "Text & threads",
    description:
      "Post text, images, video and reply-threads to Threads and reach the conversation-first audience.",
    capabilities: ["500 chars", "Media", "Threads"],
  },
  {
    id: "youtube",
    label: "YouTube",
    providerId: "youtube",
    blurb:
      "Connect your YouTube channel to upload videos and Shorts on a schedule.",
    // Google OAuth verification passed 2026-08-25 (the consent screen) and
    // the YouTube API Services audit passed 2026-09-23 (quota). The project
    // now gets 1,000 `videos.insert` calls/day and 110,000 units/day for
    // everything else, shared across every customer, for a 6-month grant —
    // reapply before 2027-03-23 or it drops back to 100 uploads/day. Watch
    // `youtube.quota_exceeded` before it becomes a support queue.
    //
    // Runtime connectability is gated by ENABLE_YOUTUBE_PROVIDER via
    // providerCapabilities — THAT flag, not this one, is what the in-app
    // connect grid reads. This entry drives the marketing surfaces and the
    // live-network count (which is also a Polar benefit bullet: re-run
    // `pnpm polar:setup` on sandbox AND prod when it changes).
    status: "live",
    tagline: "Shorts & video",
    description:
      "Schedule Shorts and video uploads with titles, descriptions and thumbnails.",
    capabilities: ["Shorts", "Video"],
  },
  {
    id: "pinterest",
    label: "Pinterest",
    providerId: "pinterest",
    blurb:
      "Connect a Pinterest business account to schedule image and video pins to your boards.",
    // Standard access granted 2026-09-25, so pins reach real boards and this
    // is no longer owner-only. Runtime connectability is gated by
    // ENABLE_PINTEREST_PROVIDER via providerCapabilities.
    status: "live",
    tagline: "Pins & boards",
    description:
      "Schedule pins to your boards with titles, descriptions and destination links.",
    capabilities: ["Pins", "Boards", "Video"],
  },
];

/**
 * Labels of every network with this marketing status, in catalog order.
 *
 * Public copy that names networks — meta descriptions, FAQ answers, and the
 * facts in `public/llms.txt` (pinned by `llms-txt.test.ts`) — reads from here so
 * it cannot fall behind the catalog. It did: the integrations description and
 * llms.txt both still named three live networks after nine had shipped.
 */
export function platformLabels(status: ChannelStatus): string[] {
  return PLATFORM_CATALOG.flatMap((channel) =>
    channel.status === status ? [channel.label] : [],
  );
}

/**
 * A live network that has not finished approving PostDaily. It works and can
 * be connected today, under the lower limits of its review stage. Read from
 * `PROVIDERS_IN_REVIEW`, the table behind the in-app warning, so the public
 * pages and the app cannot disagree: public copy counts these as live and
 * says they are in review.
 */
export function platformInReview(id: PlatformId): boolean {
  return Boolean(PROVIDERS_IN_REVIEW[id]);
}

/** Labels of the live networks still in review, in catalog order. */
export function inReviewPlatformLabels(): string[] {
  return PLATFORM_CATALOG.flatMap((channel) =>
    channel.status === "live" && platformInReview(channel.id)
      ? [channel.label]
      : [],
  );
}

/** One sentence for public copy, or "" when no network is in review. */
export function inReviewSentence(): string {
  const labels = inReviewPlatformLabels();
  const [only] = labels;

  if (!only) {
    return "";
  }

  return labels.length === 1
    ? `${only} works today while ${only} reviews PostDaily's access, so it runs under lower limits until that is approved.`
    : `${joinWithAnd(labels)} work today while those networks review PostDaily's access, so they run under lower limits until that is approved.`;
}

/**
 * Rough global-audience order, most popular first. Used to rank the connect
 * surfaces so the networks people actually ask for lead the list; the mock
 * dev channel is deliberately last. `/integrations` uses it too, so the big
 * networks lead the public page; other marketing copy keeps catalog order.
 */
export const PLATFORM_POPULARITY_ORDER: PlatformId[] = [
  "facebook",
  "instagram",
  "tiktok",
  "youtube",
  "x",
  "linkedin",
  "pinterest",
  "threads",
  "mastodon",
  "bluesky",
  "mock",
];

const POPULARITY_RANK = new Map(
  PLATFORM_POPULARITY_ORDER.map((id, index) => [id, index]),
);

/** Lower sorts first. Unknown platforms fall to the end. */
export function platformPopularityRank(id: PlatformId): number {
  return POPULARITY_RANK.get(id) ?? PLATFORM_POPULARITY_ORDER.length;
}

/**
 * Connect-surface ordering: everything connectable right now first (most
 * popular first), then the "coming soon" networks in the same popularity
 * order. Returns a new array — callers may pass a frozen/shared catalog.
 */
export function sortForConnectSurface<T>(
  items: T[],
  getId: (item: T) => PlatformId,
  isConnectable: (item: T) => boolean,
): T[] {
  return [...items].sort((a, b) => {
    const availabilityDelta =
      Number(isConnectable(b)) - Number(isConnectable(a));

    if (availabilityDelta !== 0) {
      return availabilityDelta;
    }

    return platformPopularityRank(getId(a)) - platformPopularityRank(getId(b));
  });
}

/**
 * The mock provider is a deterministic dev/test channel. It is surfaced only
 * when `providerCapabilities` reports it enabled, so production grids stay clean.
 */
export const MOCK_PLATFORM: PlatformCatalogEntry = {
  id: "mock",
  label: "Mock Social",
  providerId: "mock",
  blurb: "A deterministic test channel for local development.",
  status: "live",
  tagline: "Dev",
  description: "A deterministic test channel for local development.",
  capabilities: [],
};
